/**
 * Record links in a chat answer (MI-343).
 *
 * The assistant points at a record by writing a markdown link whose address is
 * the record: `[Restaurantes](record:category/316)`. The backend keeps a link
 * only when a tool returned that record in the conversation, so every one that
 * reaches the app is real. The app draws it (a chip, a row) from the record's
 * own data and opens the record on a click.
 *
 * Pure helpers: no React, no requests.
 */

export const RECORD_TYPES = ['movement', 'account', 'category', 'tag', 'goal', 'source_file'];

const TYPES = RECORD_TYPES.join('|');
const HREF = new RegExp(`^record:(${TYPES})/(\\d+)$`);
const LINKS = new RegExp(`\\]\\(record:(${TYPES})/(\\d+)\\)`, 'g');

/** `record:category/316` → `{ type: 'category', id: 316 }`, or null. */
export const parseRecordHref = (href) => {
  const match = HREF.exec(href || '');
  return match ? { type: match[1], id: Number(match[2]) } : null;
};

export const recordKey = (type, id) => `${type}:${id}`;

/** Every record a message's text points at, once each, in order. */
export const linkedRecords = (content) => {
  const found = [];
  const seen = new Set();
  for (const match of (content || '').matchAll(LINKS)) {
    const key = recordKey(match[1], match[2]);
    if (!seen.has(key)) {
      seen.add(key);
      found.push({ type: match[1], id: Number(match[2]) });
    }
  }
  return found;
};

// Where each kind of record lives, and the parameter its page opens it by.
const PAGES = {
  movement: ['/movements', 'movement_id'],
  account: ['/accounts', 'account_id'],
  category: ['/categories', 'category_id'],
  tag: ['/tags', 'tag_id'],
  goal: ['/goals', 'goal_id'],
  source_file: ['/source-files', 'source_file_id'],
};

/**
 * The page a record opens in: `/categories?category_id=316`, and with a
 * `month`, `/categories?category_id=316&month=2026-08`.
 */
export const recordPath = (type, id, { month } = {}) => {
  const [path, param] = PAGES[type] || [];
  if (!path) return null;
  return `${path}?${param}=${id}${month ? `&month=${month}` : ''}`;
};

/**
 * Where a click on a record goes, as `{ pathname, search }`. On the record's
 * own page the address keeps what it already says (the period and the filters
 * of Movements) and only gains the record: the page stays as the reader left
 * it, with the record open. A `month` (a category's, MI-346) replaces the one
 * the page shows.
 */
export const recordLocation = (type, id, current = window.location, { month } = {}) => {
  const [pathname, param] = PAGES[type] || [];
  if (!pathname) return null;
  const params = new URLSearchParams(current.pathname === pathname ? current.search : '');
  // Source files keeps its open statement by upload: a link by file replaces it.
  if (type === 'source_file') params.delete('process_id');
  params.set(param, id);
  if (month) params.set('month', month);
  return { pathname, search: `?${params.toString()}` };
};

/**
 * Where a category named in an answer opens (MI-346). The tool that summed it
 * handed over the period it covered (`start`, `end`):
 *
 * - one calendar month → `{ month: '2026-08' }`, the category page on that month;
 * - longer (a year, a trip) → `{ search }`, Movements filtered to the category
 *   over that period, since the category page shows one month at a time;
 * - no period, or not a category → `{}`, the record opens as it always has.
 *
 * On the phone the categories page shows the current month only, so any
 * period opens Movements: an answer about August never lands on October.
 */
export const recordDestination = (record, { phone = false } = {}) => {
  const { type, id, start, end } = record || {};
  if (type !== 'category' || !start || !end) return {};
  const month = start.slice(0, 7);
  if (end.slice(0, 7) === month && !phone) return { month };
  return {
    search: {
      start,
      end,
      filters: [
        { entity: 'Movement', column: 'category_id', operator: 'in', values: [id] },
        // The tools leave ignored movements out of a total; the page lists them unless told.
        { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] },
      ],
    },
  };
};

/**
 * The key of the record an address has open (`/categories?category_id=316` →
 * `category:316`), or null. Never a source file: its page keeps the open
 * statement by upload, and drops the file from the address once it has found it.
 */
export const recordAt = (address) => {
  const [pathname, search = ''] = String(address || '').split('?');
  const type = RECORD_TYPES.find((t) => t !== 'source_file' && PAGES[t][0] === pathname);
  const id = type ? Number(new URLSearchParams(search).get(PAGES[type][1])) : 0;
  return id > 0 ? recordKey(type, id) : null;
};

// -- reading the answer's markdown tree (hast) -----------------------------------

const isElement = (node, tag) => node?.type === 'element' && node.tagName === tag;
const isBlank = (node) => node?.type === 'text' && !(node.value || '').trim();

/** Plain text of a node. */
export const textOf = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  return (node.children || []).map(textOf).join('');
};

/** The record a link node points at, or null. */
export const recordOfLink = (node) => (
  isElement(node, 'a') ? parseRecordHref(node.properties?.href) : null
);

// What may wrap an item's one link: a loose list's paragraph, bold, italics.
const isWrapper = (node) => ['p', 'strong', 'em'].some((tag) => isElement(node, tag));

/**
 * The records of a list whose every item is one record link and nothing else:
 * that is the assistant listing records, and the app draws them as rows. Null
 * for any other list.
 */
export const recordsOfList = (list) => {
  const items = (list?.children || []).filter((child) => isElement(child, 'li'));
  if (items.length === 0) return null;
  const records = [];
  for (const item of items) {
    let content = (item.children || []).filter((child) => !isBlank(child));
    // A loose list wraps each item's text in a paragraph, and a model often
    // writes a name in bold.
    while (content.length === 1 && isWrapper(content[0])) {
      content = (content[0].children || []).filter((child) => !isBlank(child));
    }
    const record = content.length === 1 ? recordOfLink(content[0]) : null;
    if (!record) return null;
    records.push({ ...record, label: textOf(content[0]) });
  }
  return records;
};

const OWN_BLOCKS = ['p', 'li', 'ul', 'ol', 'table', 'blockquote'];

/**
 * The movements a sentence names. A movement is never a chip: it is drawn as a
 * row under the text that names it. Blocks nested inside draw their own.
 */
export const movementsNamedIn = (block) => {
  const found = [];
  const walk = (node) => {
    (node.children || []).forEach((child) => {
      if (child.type !== 'element' || OWN_BLOCKS.includes(child.tagName)) return;
      const record = recordOfLink(child);
      if (record?.type === 'movement' && !found.some((r) => r.id === record.id)) {
        found.push({ ...record, label: textOf(child) });
      }
      walk(child);
    });
  };
  walk(block || {});
  return found;
};

/** The search filter that reads exactly these movements. */
export const movementsFilter = (ids) => ({
  entity: 'Movement', column: 'id', operator: 'in', values: ids,
});

// The filters a change of each field can move a movement into or out of. A
// change of any other field (an amount, a date, a merge) may move it across
// any of them.
const MOVES_ACROSS = {
  category: [['Movement', 'category_id']],
  tags: [['Tag', 'id']],
  note: [['Movement', 'note']],
  goal: [['Goal', 'id']],
  group: [],
};

/**
 * The search a "see all" under changed movements can reuse: the one the chat
 * found them with ("Nota contiene uber", this week), as long as it lists exactly
 * those movements. It found as many as were changed, and it filters on nothing
 * the change moved; an alert may clear with any change. Null otherwise, and the
 * link lists the movements by their ids.
 */
export const searchOfChange = (movements, searches) => {
  const index = movements?.[0]?.search;
  const search = index == null ? null : searches?.[index];
  if (!search || search.count !== movements.length) return null;
  if (movements.some((m) => m.search !== index)) return null;
  const moved = [];
  const fields = new Set(movements.flatMap((m) => (m.changes || []).map((c) => c.field)));
  for (const field of fields) {
    if (!MOVES_ACROSS[field]) return null;
    moved.push(...MOVES_ACROSS[field]);
  }
  const touches = (filter) => filter.column === 'alert'
    || moved.some(([entity, column]) => filter.entity === entity && filter.column === column);
  return (search.filters || []).some(touches) ? null : search;
};

/**
 * `searchOfChange` over a conversation: in the message at `position`, which
 * changed the movements, then in the ones before it, latest first. The chat
 * asks before changing several movements, so it usually found them one message
 * earlier ("I found 3 Uber rides… move them to Transport?" · "yes").
 */
export const searchOfChangeIn = (messages, position, movements) => {
  for (let i = Math.min(position, (messages || []).length - 1); i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    const there = new Map((message.records || []).map((r) => [recordKey(r.type, r.id), r]));
    const asThere = movements.map((m) => ({ ...m, search: there.get(recordKey('movement', m.id))?.search }));
    const search = searchOfChange(asThere, message.searches);
    if (search) return search;
  }
  return null;
};

/**
 * The records of a message that something was done to, in the order the tools
 * did it, without the ones the text already points at: those are drawn where
 * the assistant put them.
 */
export const receiptsOf = (records, content) => {
  const linked = new Set(linkedRecords(content).map((r) => recordKey(r.type, r.id)));
  return (records || []).filter((r) => r.action && !linked.has(recordKey(r.type, r.id)));
};

/**
 * Where a message's receipts go: after its first paragraph when more follows
 * ("Listo, lo registré:" · receipt · "Si quieres…"), else at the end.
 * Returns `[before, after]`; `after` is '' when the receipts close the message.
 */
export const splitForReceipts = (content) => {
  const text = content || '';
  const at = text.indexOf('\n\n');
  if (at === -1) return [text, ''];
  const head = text.slice(0, at);
  const tail = text.slice(at).trim();
  // Only a plain opening sentence takes a receipt under it: a list, a table or
  // a heading is not something to cut in two.
  const plain = !/^\s*([-*+>#|]|\d+[.)]\s|```)/m.test(head);
  return plain && tail ? [head, tail] : [text, ''];
};
